import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

class CustomModelLoader {
  constructor() {
    this.customModels = [];
    this.loader = new GLTFLoader();
    
    // Setup DRACO loader
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('/draco/');
    this.loader.setDRACOLoader(dracoLoader);
    
    // Scena temporanea per generare thumbnail
    this.thumbnailScene = null;
    this.thumbnailCamera = null;
    this.thumbnailRenderer = null;
    
    this.setupThumbnailRenderer();
  }
  
  setupThumbnailRenderer() {
    // Scena per rendering off-screen
    this.thumbnailScene = new THREE.Scene();
    this.thumbnailScene.background = new THREE.Color(0x2a2a3e);
    
    // Camera
    this.thumbnailCamera = new THREE.PerspectiveCamera(45, 400 / 300, 0.1, 1000);
    
    // Luci
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.thumbnailScene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 10, 7);
    this.thumbnailScene.add(directionalLight);
    
    const fillLight = new THREE.DirectionalLight(0x88ccff, 0.3);
    fillLight.position.set(-5, 5, -5);
    this.thumbnailScene.add(fillLight);
    
    // Renderer off-screen - 400x300 pixel
    this.thumbnailRenderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: false,
      preserveDrawingBuffer: true
    });
    this.thumbnailRenderer.setSize(400, 300);
    this.thumbnailRenderer.outputEncoding = THREE.sRGBEncoding;
  }
  
  async loadCustomModel(file, metadata) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result;
          
          // Carica il modello
          const gltf = await this.loader.parseAsync(arrayBuffer, '');
          
          // Genera thumbnail
          const thumbnailDataURL = await this.generateThumbnail(gltf.scene.clone());
          
          // Crea oggetto modello custom
          const customModel = {
            id: this.generateId(),
            name: metadata.name,
            modelData: arrayBuffer, // Salva i dati binari
            thumbnail: thumbnailDataURL,
            dimensions: {
              width: parseFloat(metadata.width),
              height: parseFloat(metadata.height),
              depth: parseFloat(metadata.depth)
            },
            snapToWall: metadata.snapToWall,
            snapToGrid: metadata.snapToGrid,
            rotationStep: parseInt(metadata.rotationStep) || 45,
            isCustom: true
          };
          
          this.customModels.push(customModel);
          
          console.log('✓ Modello custom caricato:', customModel.name);
          resolve(customModel);
        } catch (error) {
          console.error('Errore caricamento modello custom:', error);
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Errore lettura file'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  }
  
  async generateThumbnail(modelScene) {
    // Calcola bounding box
    const box = new THREE.Box3().setFromObject(modelScene);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    
    // Posiziona il modello al centro
    modelScene.position.sub(center);
    this.thumbnailScene.add(modelScene);
    
    // Calcola la distanza ottimale della camera
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = this.thumbnailCamera.fov * (Math.PI / 180);
    let cameraDistance = Math.abs(maxDim / Math.sin(fov / 2)) * 1.2;
    
    // Posiziona camera con angolazione isometrica
    this.thumbnailCamera.position.set(
      cameraDistance * 0.7,
      cameraDistance * 0.8,
      cameraDistance * 0.7
    );
    this.thumbnailCamera.lookAt(0, size.y * 0.3, 0);
    
    // Render
    this.thumbnailRenderer.render(this.thumbnailScene, this.thumbnailCamera);
    
    // Estrae immagine come data URL
    const dataURL = this.thumbnailRenderer.domElement.toDataURL('image/png');
    
    // Pulisce la scena
    this.thumbnailScene.remove(modelScene);
    modelScene.traverse((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(mat => mat.dispose());
        } else {
          child.material.dispose();
        }
      }
    });
    
    return dataURL;
  }
  
  generateId() {
    return 'custom_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
  
  getCustomModels() {
    return this.customModels;
  }
  
  removeCustomModel(id) {
    const index = this.customModels.findIndex(m => m.id === id);
    if (index !== -1) {
      this.customModels.splice(index, 1);
      return true;
    }
    return false;
  }
  
  clearAllCustomModels() {
    this.customModels = [];
  }
  
  // Metodo per caricare un modello custom già in memoria (usato dal FurnitureController)
  async loadModelFromData(modelData) {
    return new Promise((resolve, reject) => {
      this.loader.parse(
        modelData,
        '',
        (gltf) => {
          resolve(gltf);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }
  
  dispose() {
    if (this.thumbnailRenderer) {
      this.thumbnailRenderer.dispose();
    }
    
    if (this.thumbnailScene) {
      this.thumbnailScene.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => mat.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
    }
    
    this.customModels = [];
  }
}

export default CustomModelLoader;